import {useCallback, useMemo} from 'react';
import {dispatcher} from '@netx/core-next/instance/dispatcher';
import {useAttributesStore} from '../store';
import {useConstCallback} from '@netx/core-components/hooks/useConstCallback';
import {SYSTEM_ATTRIBUTE_LABELS} from '../locales';
import type {AttributeDefaultValues, Lookups} from '../types';
import {
	type IAttributeTemplate,
	type AttributeTemplateType,
	type AttributeTemplateObjectType,
} from '../schema';
import parseValue from '../utils/parseValue';
import {useMetadataLookups} from './useMetadataLookups';

const defaultParse = (attributes: IAttributeTemplate[]) => attributes;

export type UseAttributesParams = {
	objectType?: AttributeTemplateObjectType;
	omitTypes?: AttributeTemplateType[];
	writableOnly?: boolean;
	omitSystem?: boolean;
	omitIds?: number[];
	limitToIds?: number[];
	limitToTypes?: readonly AttributeTemplateType[] | AttributeTemplateType[];
	parse?: (attributes: IAttributeTemplate[]) => IAttributeTemplate[];
};

export type CommonUseAttributeOptions = {
	mandatoryOnly?: boolean;
	excludeMandatory?: boolean;
	objectType?: AttributeTemplateObjectType;
	omitSystem?: boolean;
	omitReadOnly?: boolean;
	omitTypes?: AttributeTemplateType[];
	limitToTypes?: AttributeTemplateType[];
	onlyTypes?: AttributeTemplateType[];
	limitToIds?: number[];
	omitIds?: number[];
};

export const useAttributes = ({
	limitToIds,
	limitToTypes,
	objectType,
	omitIds,
	omitTypes,
	writableOnly = false,
	omitSystem = false,
	parse = defaultParse,
}: UseAttributesParams = {}) => {
	// TODO: until we can wrap the entire app in we just need to utilize the singleton
	// and provide some bridging between that and the context
	const {
		attributes: storeAttributes,
		loading,
		userAttributes,
		systemAttributes,
	} = useAttributesStore();

	const attributes = useMemo(() => {
		return parse(
			storeAttributes.filter(
				(attr) =>
					(!objectType || attr.objectTypes.includes(objectType)) &&
					(!limitToIds || limitToIds.includes(attr.attributeid)) &&
					(!omitTypes || !omitTypes.includes(attr.type)) &&
					(!limitToTypes || limitToTypes.includes(attr.type)) &&
					(!omitSystem || attr.attributeid > -1) &&
					(!omitIds || !omitIds.includes(attr.attributeid)) &&
					(!writableOnly || attr.permission !== 'r'),
			),
		);
	}, [
		storeAttributes,
		limitToIds,
		limitToTypes,
		writableOnly,
		objectType,
		omitSystem,
		omitTypes,
		omitIds,
		parse,
	]);

	const attributeIds = useMemo(
		() => attributes.map((attr) => attr.attributeid),
		[attributes],
	);

	const hasMandatoryUserDefinedTemplates = useMemo(
		() => attributes.some((d) => !d.system && d.mandatory === 1),
		[attributes],
	);

	const attributesTrackingHistory = useMemo(() => {
		return attributes.filter((attr) => attr.trackHistory);
	}, [attributes]);

	const trackingHistory = useMemo(() => {
		return attributesTrackingHistory.length > 0;
	}, [attributesTrackingHistory]);

	const lookups = useMemo(() => {
		return attributes.reduce<Lookups>(
			(lookups, attr) => {
				if (attr.keyDatasets.length) lookups.keys.push(attr.attributeid);
				if (attr.targetDatasets.length) lookups.targets.push(attr.attributeid);
				return lookups;
			},
			{
				keys: [],
				targets: [],
			},
		);
	}, []);

	// Some basic getters

	/**
	 * Get all attribute templates
	 * @param omitReadOnly Omit read only attribute templates
	 * @return Array of attribute templates
	 */
	const getAll = useConstCallback((options: CommonUseAttributeOptions = {}) => {
		return attributes.filter(
			(attr) =>
				(!options.limitToIds ||
					options.limitToIds.includes(attr.attributeid)) &&
				(!options.limitToTypes || options.limitToTypes.includes(attr.type)) &&
				(!options.omitTypes || !options.omitTypes.includes(attr.type)) &&
				(!options.onlyTypes || options.onlyTypes?.includes(attr.type)) &&
				(!options.omitIds || !options.omitIds?.includes(attr.attributeid)) &&
				(!options.excludeMandatory || !attr.mandatory) &&
				(!options.mandatoryOnly || attr.mandatory) &&
				(!options.omitReadOnly || attr.permission !== 'r') &&
				(!options.omitSystem || !attr.system) &&
				(!options.objectType || attr.objectTypes.includes(options.objectType)),
		);
	});

	const getAllAsObject = useConstCallback(
		(options: CommonUseAttributeOptions = {}) => {
			return getAll(options).reduce<Record<number, IAttributeTemplate>>(
				(accum, attr) => ({
					...accum,
					[attr.attributeid]: attr,
				}),
				{},
			);
		},
	);

	/**
	 * get attribute template by id
	 * @param id Attribute template id
	 * @return Attribute template, if found
	 */
	const getById = useConstCallback((id: number) =>
		attributes.find((attr) => attr.attributeid === id),
	);

	/**
	 * get attribute templates by ids
	 * @param ids Attribute template ids
	 * @return Array of attribute templates found
	 */
	const getByIds = useConstCallback(
		(ids: number[], options?: CommonUseAttributeOptions) =>
			getAll(options)
				.filter((attr) => ids.includes(attr.attributeid))
				.sort(
					(a, b) => ids.indexOf(a.attributeid) - ids.indexOf(b.attributeid),
				),
	);

	const getByIdsAsObject = useConstCallback(
		(ids: number[], options?: CommonUseAttributeOptions) =>
			getByIds(ids, options).reduce<Record<number, IAttributeTemplate>>(
				(accum, attr) => ({
					...accum,
					[attr.attributeid]: attr,
				}),
				{},
			),
	);

	/**
	 * get attribute template by name\
	 * _frowned upon but still neccessary in some cases_
	 * @param name Attribute template name
	 * @return Attribute template, if found
	 */
	const getByName = useConstCallback((name: string) =>
		attributes.find((attr) => attr.name === name),
	);

	/**
	 * get attribute templates by names\
	 * _frowned upon but still neccessary in some cases_
	 * @param names Attribute template names
	 * @return Array of attribute templates found
	 */
	const getByNames = useConstCallback(
		(names: string[]) =>
			names
				.map((name) => getByName(name))
				.filter((attr) => attr != null) as IAttributeTemplate[],
	);

	const getByType = useConstCallback(
		(type: AttributeTemplateType, options?: {omitSystem?: boolean}) =>
			attributes.filter(
				(attr) =>
					attr.type === type && (attr.attributeid > -1 || !options?.omitSystem),
			),
	);

	/**
	 * get attribute template label by id
	 * @param id Attribute template id
	 * @return Attribute template label, if found
	 */
	const getLabelById = useConstCallback((id: number) => {
		const attr = getById(id);
		if (!attr) return `!${id} unknown!`;
		if (!attr.system) return attr.name;
		return SYSTEM_ATTRIBUTE_LABELS[id] || i18n.t(`i18n.${attr.name}`);
	});

	/**
	 * get default attribute template values
	 * @param limitToIds Limit the returned values to the provided attribute template ids
	 * @return Attribute values indexed by id
	 */
	const getDefaultValues = useConstCallback(
		(options?: {limitToIds?: number[]; includeEmpty?: boolean}) => {
			const {includeEmpty, limitToIds} = options || {};
			const defaults = attributes.reduce<AttributeDefaultValues>(
				(defaultValues, d) => {
					if (
						(limitToIds && !limitToIds.includes(d.attributeid)) ||
						(!includeEmpty && (d.value == null || d.value === ''))
					)
						return defaultValues;

					const value = parseValue(d.type, d.value);
					return {
						...defaultValues,
						[d.attributeid]: value as string[], // parseValue(d.type, d.value) as string[], // TODO: get the attribute values wrangled
					};
				},
				{},
			);
			return defaults;
		},
	);

	const {lookup, attributesWorking, canLookup} = useMetadataLookups({
		attributeTemplates: attributes,
		attributeIds,
	});

	/**
	 * search by attribute value
	 * @param id Attribute template id to search from
	 * @param value Value to search with
	 */
	const searchValue = useCallback((id: number, value: any) => {
		const attributeTemplate = getById(id);
		if (!attributeTemplate) return;
		dispatcher.trigger('search:attribute:value', attributeTemplate.name, value);
	}, []);

	return {
		data: attributes,
		attributeIds,
		attributesWorking,
		hasMandatoryUserDefinedTemplates,
		total: attributes.length,
		loading,
		lookups,
		metadataLookupsEnabled: canLookup,
		attributesTrackingHistory,
		trackingHistory,

		getAll,
		getAllAsObject,
		getById,
		getByIds,
		getByIdsAsObject,
		getByName,
		getByNames,
		getByType,
		getDefaultValues,
		getLabelById,
		lookup,
		searchValue,
	};
};

export default useAttributes;
